'use client';
import type { CSSProperties } from 'react';

import { buildUrl } from '@connected/api-client/lib/portal';
import { Popover, PopoverContent, PopoverTrigger } from '@connected/ui/components/popover';
import { format, isPast } from 'date-fns';
import { MapPin, MoveUpRight, UsersRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CalendarEvent } from '@/components/event-calendar';

import {
    getBorderRadiusClasses,
    getEventColorClasses,
    getEventColorInlineStyle,

} from '@/components/event-calendar';
import { cn } from '@/lib/utils';

// 24-hour time format HH:mm
function formatTimeWithOptionalMinutes(date: Date) {
    return format(date, 'HH:mm');
}

type EventWrapperProps = {
    event: CalendarEvent;
    isFirstDay?: boolean;
    isLastDay?: boolean;
    isDragging?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    className?: string;
    children: React.ReactNode;
    currentTime?: Date;
    onMouseDown?: (e: React.MouseEvent) => void;
    onTouchStart?: (e: React.TouchEvent) => void;
    style?: CSSProperties;
};

// Shared wrapper component for event styling
function EventWrapper({
    event,
    isFirstDay = true,
    isLastDay = true,
    isDragging,
    onClick,
    className,
    children,
    currentTime,
    onMouseDown,
    onTouchStart,
    style,
}: EventWrapperProps) {
    const endOrStart = event.end ? new Date(event.end) : new Date(event.start);
    const displayEnd = currentTime ? new Date(new Date(currentTime).getTime() + (endOrStart.getTime() - new Date(event.start).getTime())) : endOrStart;

    const isEventInPast = isPast(displayEnd);

    const customColorStyle = getEventColorInlineStyle(event.color, { forceDarkText: event.useDarkText });

    return (
        <button
            type="button"
            className={cn(
                'focus-visible:border-ring focus-visible:ring-ring/50 flex h-full w-full overflow-hidden px-1.5 py-1.5 text-left font-medium backdrop-blur-md transition outline-none select-none focus-visible:ring-[3px] data-dragging:cursor-grabbing data-dragging:shadow-lg data-past-event:line-through sm:px-2 sm:py-2',
                getEventColorClasses(event.color),
                getBorderRadiusClasses(isFirstDay, isLastDay),
                className,
            )}
            data-dragging={isDragging || undefined}
            data-past-event={isEventInPast || undefined}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            style={style ?? customColorStyle}
        >
            {children}
        </button>
    );
}

type EventItemProps = {
    event: CalendarEvent;
    view: 'month' | 'week' | 'day' | 'agenda';
    isDragging?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    showTime?: boolean;
    currentTime?: Date; // For updating time during drag
    isFirstDay?: boolean;
    isLastDay?: boolean;
    children?: React.ReactNode;
    className?: string;
    onMouseDown?: (e: React.MouseEvent) => void;
    onTouchStart?: (e: React.TouchEvent) => void;
    hideContent?: boolean;
    isSelected?: boolean;
};

export function EventItem({
    event,
    view,
    isDragging,
    onClick,
    // showTime,
    currentTime,
    isFirstDay = true,
    isLastDay = true,
    children,
    className,
    onMouseDown,
    onTouchStart,
    hideContent = false,
    isSelected = false,
}: EventItemProps) {
    const eventColor = event.color;
    const { t } = useTranslation('dashboard');
    const customColorStyle = useMemo(() => {
        return getEventColorInlineStyle(event.color, { forceDarkText: event.useDarkText });
    }, [event.color, event.useDarkText]);

    // Use the provided currentTime (for dragging) or the event's actual time
    const displayStart = useMemo(() => {
        return currentTime || new Date(event.start);
    }, [currentTime, event.start]);

    const displayEnd = useMemo(() => {
        const endOrStart = event.end ? new Date(event.end) : new Date(event.start);
        return currentTime
            ? new Date(new Date(currentTime).getTime() + (endOrStart.getTime() - new Date(event.start).getTime()))
            : endOrStart;
    }, [currentTime, event.start, event.end]);

    // Calculate event duration in minutes
    // const durationMinutes = useMemo(() => {
    //     // If end is missing, assume a short 30-minute event for layout decisions
    //     const minutes = differenceInMinutes(displayEnd, displayStart);
    //     return Number.isFinite(minutes) && minutes >= 0 ? minutes : 30;
    // }, [displayStart, displayEnd]);

    // const getEventTime = () => {
    //     if (event.allDay)
    //         return 'All day';

    //     // For short events (less than 45 minutes), only show start time
    //     if (durationMinutes < 45) {
    //         return formatTimeWithOptionalMinutes(displayStart);
    //     }

    //     // For longer events, show both start and end time
    //     return `${formatTimeWithOptionalMinutes(displayStart)} - ${formatTimeWithOptionalMinutes(displayEnd)}`;
    // };

    const [popoverOpen, setPopoverOpen] = useState(false);
    const isPopoverVisible = popoverOpen && isSelected;
    const isActive = isSelected;

    const rawProjectName = event.projectName ?? event.title;
    const projectName = rawProjectName.trim();
    const showProjectName = projectName.length > 0;
    const inspectionKindLabel = event.inspectionKind?.trim() ?? '';
    const inspectionDisplayLabel = inspectionKindLabel || '-';
    const startTimeLabel = formatTimeWithOptionalMinutes(displayStart);
    const startIsMidnight = displayStart.getHours() === 0 && displayStart.getMinutes() === 0;
    const shouldShowTime = !event.allDay && !startIsMidnight;
    const primaryTextTone = isActive
        ? 'text-white'
        : event.useDarkText
            ? 'text-foreground/80'
            : 'text-white/90';
    const secondaryTextTone = isActive
        ? 'text-white/90'
        : event.useDarkText
            ? 'text-foreground/60'
            : 'text-white/80';
    const nameTextTone = isActive
        ? 'text-white'
        : event.useDarkText
            ? 'text-foreground'
            : 'text-white';
    const handleTriggerClick = (eventClick: React.MouseEvent<HTMLDivElement>) => {
        onClick?.(eventClick);
    };

    const formatDateTime = (date: Date) => {
        if (event.allDay)
            return format(date, 'd MMM yyyy');
        return format(date, 'd MMM yyyy HH:mm');
    };

    const dateRangeLabel = (() => {
        const startLabel = formatDateTime(displayStart);
        if (!event.end)
            return startLabel;

        const hasDistinctEnd = new Date(event.end).getTime() !== new Date(event.start).getTime();
        if (!hasDistinctEnd)
            return startLabel;

        const endLabel = formatDateTime(displayEnd);
        return `${startLabel} - ${endLabel}`;
    })();

    const inlineEventDetails = (
        <span className="flex w-full min-w-0 flex-col gap-[2px]">
            <span
                className={cn(
                    'flex items-center text-[9px] font-semibold uppercase leading-tight tracking-tight sm:text-[10px]',
                    primaryTextTone,
                    shouldShowTime ? 'gap-1' : 'gap-0',
                )}
            >
                <span className="truncate pt-1">{inspectionDisplayLabel}</span>
                {shouldShowTime && (
                    <span className={cn('shrink-0 pt-1 text-[9px] font-medium leading-tight sm:text-[10px]', secondaryTextTone)}>
                        {startTimeLabel}
                    </span>
                )}
            </span>
            {showProjectName && (
                <span className={cn('truncate text-[10px] font-medium pb-1 leading-tight sm:text-[11px]', nameTextTone)}>
                    {projectName}
                </span>
            )}
        </span>
    );
    if (view === 'month') {
        const content = children || inlineEventDetails;

        return (
            <Popover open={isPopoverVisible} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                    <div onClick={handleTriggerClick}>
                        <EventWrapper
                            event={event}
                            isFirstDay={isFirstDay}
                            isLastDay={isLastDay}
                            isDragging={isDragging}
                            className={cn(
                                'mt-[var(--event-gap)] items-center text-[10px] sm:text-xs',
                                isActive && '!bg-brand-primary !text-white',
                                isSelected && 'ring-2 ring-brand-primary ring-offset-2 ring-offset-background',
                                className,
                                'h-[var(--event-height)]',
                            )}
                            currentTime={currentTime}
                            onMouseDown={onMouseDown}
                            onTouchStart={onTouchStart}
                            style={customColorStyle}
                        >
                            {hideContent
                                ? (
                                        <>
                                            <span className="sr-only">{event.title}</span>
                                            <div aria-hidden className="invisible">
                                                {content}
                                            </div>
                                        </>
                                    )
                                : content}
                        </EventWrapper>
                    </div>
                </PopoverTrigger>
                <PopoverContent align="start" side="bottom" className="w-80 rounded-lg p-4 ">
                    <div className="flex flex-col">
                        <div className="font-semibold text-lg">{event.title}</div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                            {inspectionDisplayLabel}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-sm mb-1">
                            {event.description && (
                                <span className="font-medium">{event.description}</span>
                            )}
                            <span className="text-muted-foreground text-xs">{dateRangeLabel}</span>
                        </div>
                        {event.location?.Locality && event.location?.Street && event.location?.StreetNumber && event.location?.PostalCode && (
                            <div className="flex flex-row gap-2 mt-1">
                                <MapPin size={16} className="mt-0.5 text-foreground/60" />
                                <div className="text-sm flex items-start gap-2">
                                    <span>{`${event.location.Street} ${event.location.StreetNumber}, ${event.location.PostalCode} ${event.location.Locality}`}</span>
                                </div>
                            </div>
                        )}
                        {Array.isArray(event.inspectors) && event.inspectors.length > 0 && (
                            <div className="flex flex-row gap-2 mt-0.5">
                                <UsersRound size={18} className="mt-0.5 text-foreground/60 inline" />
                                <div className="text-sm inline gap-2">
                                    <span>
                                        {(() => {
                                            const names = event.inspectors.map(i => i?.DisplayName).filter(Boolean);
                                            if (names.length > 3) {
                                                return `${names.slice(0, 3).join(', ')}...`;
                                            }
                                            return names.join(', ');
                                        })()}
                                    </span>
                                </div>
                            </div>

                        )}
                        <a
                            href={`${buildUrl(import.meta.env.VITE_DOMAIN_ELEKTRA_APP, `/projects/${event.id}/applications/request-concerning`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <p
                                className="mt-3 self-start text-sm text-brand-primary font-semibold hover:cursor-pointer"
                            >
                                {t('dashboard.planning.message.openInElectra')}
                                <MoveUpRight size={16} className="inline ml-1" />
                            </p>
                        </a>
                    </div>
                </PopoverContent>
            </Popover>
        );
    }

    if (view === 'week') {
        return (
            <Popover open={isPopoverVisible} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                    <div onClick={handleTriggerClick}>
                        <EventWrapper
                            event={event}
                            isFirstDay={isFirstDay}
                            isLastDay={isLastDay}
                            isDragging={isDragging}
                            className={cn(
                                'mt-[var(--event-gap)] items-center text-[10px] sm:text-xs',
                                isActive && '!bg-brand-primary !text-white',
                                isSelected && 'ring-2 ring-brand-primary ring-offset-2 ring-offset-background',
                                className,
                                'h-[var(--event-height)]',
                            )}
                            currentTime={currentTime}
                            onMouseDown={onMouseDown}
                            onTouchStart={onTouchStart}
                            style={customColorStyle}
                        >
                            {children || inlineEventDetails}
                        </EventWrapper>
                    </div>
                </PopoverTrigger>
                <PopoverContent align="start" side="bottom" className="w-80 rounded-lg p-4 ">
                    <div className="flex flex-col">
                        <div className="font-semibold text-lg">{event.title}</div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                            {inspectionDisplayLabel}
                        </div>
                        <div className="flex flex-wrap items-center gap-2 text-sm mb-1">
                            {event.description && (
                                <span className="font-medium">{event.description}</span>
                            )}
                            <span className="text-muted-foreground text-xs">{dateRangeLabel}</span>
                        </div>
                        {event.location?.Locality && event.location?.Street && event.location?.StreetNumber && event.location?.PostalCode && (
                            <div className="flex flex-row gap-2 mt-1">
                                <MapPin size={16} className="mt-0.5 text-foreground/60" />
                                <div className="text-sm flex items-start gap-2">
                                    <span>{`${event.location.Street} ${event.location.StreetNumber}, ${event.location.PostalCode} ${event.location.Locality}`}</span>
                                </div>
                            </div>
                        )}
                        {Array.isArray(event.inspectors) && event.inspectors.length > 0 && (
                            <div className="flex flex-row gap-2 mt-0.5">
                                <UsersRound size={18} className="mt-0.5 text-foreground/60 inline" />
                                <div className="text-sm inline gap-2">
                                    <span>
                                        {(() => {
                                            const names = event.inspectors.map(i => i?.DisplayName).filter(Boolean);
                                            if (names.length > 3) {
                                                return `${names.slice(0, 3).join(', ')}...`;
                                            }
                                            return names.join(', ');
                                        })()}
                                    </span>
                                </div>
                            </div>
                        )}
                        <a
                            href={`${buildUrl(import.meta.env.VITE_DOMAIN_ELEKTRA_APP, `/projects/${event.id}/applications/request-concerning`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <p
                                className="mt-3 self-start text-sm text-brand-primary font-semibold hover:cursor-pointer"
                            >
                                {t('dashboard.planning.message.openInElectra')}
                                <MoveUpRight size={16} className="inline ml-1" />
                            </p>
                        </a>
                    </div>
                </PopoverContent>
            </Popover>
        );
    }
    return (
        <div
            className={cn(
                'focus-visible:border-ring focus-visible:ring-ring/50 flex w-full flex-col gap-1 rounded p-2 text-left transition outline-none focus-visible:ring-[3px] data-past-event:line-through data-past-event:opacity-90',
                getEventColorClasses(eventColor),
                isSelected && 'ring-2 ring-brand-primary ring-offset-2 ring-offset-background',
                className,
            )}
            data-past-event={event.end ? isPast(new Date(event.end)) || undefined : undefined}
            onClick={onClick}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            style={customColorStyle}
        >
            <div className="text-sm font-medium">
                {event.title}
                {' '}
                <div className="text-xs opacity-70">
                    {event.allDay
                        ? (
                                <span>No date</span>
                            )
                        : event.end
                            ? (
                                    <span className="uppercase">
                                        {formatTimeWithOptionalMinutes(displayStart)}
                                        {' '}
                                        {' - '}
                                        {' '}
                                        {formatTimeWithOptionalMinutes(displayEnd)}
                                    </span>
                                )
                            : (
                                    <span className="uppercase">
                                        {formatTimeWithOptionalMinutes(displayStart)}
                                    </span>
                                )}
                </div>
            </div>
            {event.description && (
                <div className="text-xs opacity-90">{event.description}</div>
            )}
            {event.location?.Locality && event.location?.Street && event.location?.StreetNumber && event.location?.PostalCode && (
                <div className="text-xs">
                    <span>{`${event.location.Street} ${event.location.StreetNumber}, ${event.location.PostalCode} ${event.location.Locality}`}</span>
                </div>
            )}
            <a
                href={`${buildUrl(import.meta.env.VITE_DOMAIN_ELEKTRA_APP, `/projects/${event.id}/applications/request-concerning`)}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                <p
                    className="mt-3 self-start text-sm text-brand-primary font-semibold hover:cursor-pointer"
                >
                    {t('dashboard.planning.message.openInElectra')}
                    <MoveUpRight size={16} className="inline ml-1" />
                </p>
            </a>
        </div>
    );
}
